/**
 * Upper bound (in px) applied when a caller renders at a non-pixel size (e.g.
 * `width="100%"`) and we can't derive a target resolution. Without it, the
 * resizer is asked to transcode the source at full resolution; multi-megapixel
 * originals then exceed imgproxy's processing timeout and return 503 (a
 * permanently broken image). Sized generously so it never visibly softens any
 * in-app preview, while keeping every resize cheap.
 */
export const DEFAULT_MAX_RESIZE_DIMENSION = 1536;

/**
 * Applies that upper bound.
 *
 * Shared by the two paths that resolve an ad image to `/agents/files`:
 * `AdStudioImage` for a plain asset, and `AdAsset` for each `ad:` ref inside an
 * ad's markup. Both must cap, or a caller sizing with `width="100%"` reaches
 * the resizer bounded through one and unbounded through the other.
 *
 * Height is passed through untouched: a caller that gives only a height has
 * already told the resizer what to target.
 */
export function boundedResizeDimensions(
  width: number | undefined,
  height: number | undefined,
): { width: number | undefined; height: number | undefined } {
  const hasExplicitDimension = width !== undefined || height !== undefined;
  return {
    width: hasExplicitDimension ? width : DEFAULT_MAX_RESIZE_DIMENSION,
    height,
  };
}
